import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { LoadRequest, SortDirection } from '@vendasta/va-filter2-table';
import {
  Order,
  CustomerSalesOrdersService,
  ListCustomerSalesOrderRequestFilters,
  ListCustomerSalesOrderRequestSortOption,
  ListCustomerSalesOrderRequestSortOptionInterface,
  ListCustomerSalesOrderRequestFiltersInterface,
  ListCustomerSalesOrderRequestSortField,
  ListCustomerSalesOrderRequestSortDirection,
} from '@vendasta/sales-orders';
import { ForcedFilterOptions, OrdersRow, BaseSalesOrdersTableService } from './interface';
import { calculateContractDurationMonths } from './conversion-utils';
import { PARTNER_ID_TOKEN } from '../external-dependencies/provider-tokens';
import { SortSetting } from '@vendasta/va-filter2-table';

@Injectable()
export class CustomerSalesOrdersTableService implements BaseSalesOrdersTableService {
  private readonly totalResults$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly totalResults$: Observable<number> = this.totalResults$$.asObservable();

  private forcedFilterOptions: ForcedFilterOptions;

  constructor(
    private readonly salesOrderService: CustomerSalesOrdersService,
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
  ) {}

  initialize(forcedFilterOptions: ForcedFilterOptions): void {
    this.forcedFilterOptions = forcedFilterOptions;
  }

  /* tslint:disable */
  static convertFilters(filters: any, forcedBusiness: string): ListCustomerSalesOrderRequestFilters {
    const f: ListCustomerSalesOrderRequestFiltersInterface = {};
    f.created = {};
    f.requestedActivation = {};
    f.expiryDate = {};

    if (filters['submittedDateFromControl']) {
      f.created.beginRange = filters['submittedDateFromControl'];
    }
    if (filters['submittedDateToControl']) {
      f.created.endRange = filters['submittedDateToControl'];
    }

    if (filters['contractStartFrom']) {
      f.requestedActivation.beginRange = filters['contractStartFrom'];
    }
    if (filters['contractStartToControl']) {
      f.requestedActivation.endRange = filters['contractStartToControl'];
    }

    if (filters['expiryDateFrom']) {
      f.expiryDate.beginRange = filters['expiryDateFrom'];
    }
    if (filters['expiryDateTo']) {
      f.expiryDate.endRange = filters['expiryDateTo'];
    }

    // Forced business filter
    f.businessId = forcedBusiness;

    return new ListCustomerSalesOrderRequestFilters(f);
  }

  static convertSortOptions(sort: SortSetting[]): ListCustomerSalesOrderRequestSortOption {
    let s: ListCustomerSalesOrderRequestSortOptionInterface = null;
    if (!!sort && sort.length > 0) {
      s = {};
      s.direction =
        sort[0].direction == SortDirection.SORT_DIRECTION_ASCENDING
          ? ListCustomerSalesOrderRequestSortDirection.ASCENDING
          : ListCustomerSalesOrderRequestSortDirection.DESCENDING;
      switch (sort[0].field) {
        case 'status':
          s.field = ListCustomerSalesOrderRequestSortField.STATUS;
          break;
        case 'requestedActivation':
          s.field = ListCustomerSalesOrderRequestSortField.REQUESTED_ACTIVATION;
          break;
        case 'created':
          s.field = ListCustomerSalesOrderRequestSortField.CREATED;
          break;
        case 'expiry':
          s.field = ListCustomerSalesOrderRequestSortField.EXPIRY_DATE;
          break;
        default:
          s = null;
      }
    }
    if (s) {
      return new ListCustomerSalesOrderRequestSortOption(s);
    }
    return null;
  }
  /* tslint:enable */

  load(request: LoadRequest): Observable<OrdersRow[]> {
    const sorting = CustomerSalesOrdersTableService.convertSortOptions(request.sortingOptions);
    const cursor = btoa((request.pageIndex * request.pageSize).toString());
    return combineLatest([this.partnerId$]).pipe(
      switchMap(([partnerId]) => {
        const filters = CustomerSalesOrdersTableService.convertFilters(
          request.filters,
          this.forcedFilterOptions.businessId,
        );
        const apiFilters = new ListCustomerSalesOrderRequestFilters(filters);
        return this.salesOrderService.list(partnerId, apiFilters, sorting, cursor, request.pageSize);
      }),
      take(1),
      map((results) => {
        if (!results || !results.results) {
          this.totalResults$$.next(results.totalResults);
          return [];
        }
        this.totalResults$$.next(results.totalResults);
        return results.results.map((o: Order) => ({
          orderId: o.orderId,
          salespersonId: o.salespersonId,
          salesperson: o.salesperson,
          partnerId: o.partnerId,
          businessId: o.businessId,
          business: o.business,
          marketId: o.marketId,
          status: o.status,
          requestedActivation: o.requestedActivation,
          created: o.created,
          tags: o.tags,
          contractDurationInMonths: calculateContractDurationMonths(o.contractDuration),
          expiryDate: o.expiryDate,
        }));
      }),
    );
  }
}
